// src/components/KpiTile.jsx
// Shared KPI tile used by OfficesPage, LeasesPage, and MarketPlanPage.
// Extracted from src/v2/locations/OfficesPage.jsx — visual output is identical.

import React, { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LAYERS, T } from "../app/tokens.js";
import { UniversalSearchOpenContext } from "../contexts/UniversalSearchContext.jsx";
import { useKpiPopoverRow } from "./KpiPopoverRowContext.jsx";
import { useCountUp } from "../v2/hooks/useCountUp.js";
import { KPI_TILE_ROW_HEIGHT } from "../features/plan/KpiTileRow.jsx";
import { fmtInt } from "../utils/format.js";
import { Tooltip } from "./Tooltip.jsx";
import { KpiBandCollectionContext, KpiBandPresentationContext } from "../contexts/KpiBandContext.jsx";
import { CondensedKpiMetric } from "./CondensedKpiMetric.jsx";
import { useHoverIntent } from "./useHoverIntent.js";

// KPI-SCOPE-INDICATOR (G-22): inline funnel SVG glyph.
// Shared so it is not duplicated across tiles. When `filtered` is true the fill
// switches from the faint muted tone to the accent blue.
export function FunnelIcon({ filtered = false }) {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 12 12"
      style={{ flexShrink: 0, display: "block" }}
      aria-hidden="true"
    >
      <path
        d="M1 1h10L7 6v4l-2 1V6z"
        fill={filtered ? "var(--v2-accent)" : "var(--v2-light)"}
      />
    </svg>
  );
}

// KPI-TILE-BALANCE-FIXED-HEIGHT (G-9): three-zone layout — label zone reserves
// exactly two lines (height:32) so every number sits on the same baseline
// regardless of whether the label wraps; sub-text is pinned to the bottom via
// marginTop:auto; outer wrapper uses height (not minHeight) when a pixel value
// is supplied so absolute tooltip/breakdown panels position correctly.
// MARKET-PLAN-WHATIF-TILE-HEIGHT-MATCH-S2: flex-column layout + overflow:hidden
// ensures this tile never grows taller than the shared KPI_TILE_ROW_HEIGHT.
export function KpiTile({
  label,
  value,
  rawValue,
  formatter = (n) => fmtInt(n),
  animationKey = 0,
  tileIndex = 0,
  countFromZero = false,
  sub,
  secondary,
  descriptor,
  labelStyle,
  valueStyle,
  color,
  testId,
  // Optional hard pixel height. When provided (e.g. Market Plan passes
  // KPI_TILE_ROW_HEIGHT), the tile is boxed at exactly that height with
  // overflow clipped, so long/wrapping label or sub text can never force
  // the CSS Grid row taller than the row's own declared height — a grid
  // row with implicit "auto" tracks otherwise sizes to the tallest item's
  // min-content, which can exceed a fixed container height and blow the
  // whole KpiTileRow out past its intended size (MARKET-PLAN-WHATIF-TILE-
  // HEIGHT-MATCH-S3). Left undefined, OfficesPage keeps its original
  // natural-content-height behavior.
  height,
  // Optional breakdown: array of { label, value } rows shown in a floating
  // panel below the tile on hover. Does not affect tile height — the panel
  // floats above the document flow via position:absolute on a relative wrapper.
  breakdown,
  // Optional explanation shown in a floating panel on hover or keyboard focus.
  // It can be a string or structured React content.
  tooltip,
  tooltipStyle,
  tooltipPlacement,
  onActivate,
  activationLabel,
  infoTooltip,
  // KPI-SCOPE-INDICATOR (G-22): when "responsive", show a funnel cue alongside
  // the descriptor. When "portfolio", show the descriptor with no cue. When a
  // descriptor is absent, fall back to the legacy scope/sub presentation.
  scope,
  // KPI-SCOPE-INDICATOR (G-22): true when a filter or search is actively
  // narrowing the view — turns funnel + sub-label blue and adds a soft blue ring.
  isFiltered = false,
}) {
  const [hovered, setHovered] = useState(false);
  const condensed = useContext(KpiBandPresentationContext);
  const collection = useContext(KpiBandCollectionContext);
  const [portalPosition, setPortalPosition] = useState(null);
  const [tooltipAnchorLeft, setTooltipAnchorLeft] = useState(null);
  const wrapperRef = useRef(null);
  const searchOpen = useContext(UniversalSearchOpenContext);
  const generatedId = useId();
  const rowPopover = useKpiPopoverRow();
  const explanation = tooltip || infoTooltip;
  const panelId = testId ? `${testId}-tooltip` : `kpi-tile-tooltip-${generatedId}`;
  const tileId = testId ?? generatedId;
  const hasPopover = (explanation != null && (typeof explanation !== "string" || explanation.length > 0))
    || (Array.isArray(breakdown) && breakdown.length > 0);
  const intent = useHoverIntent({
    enabled: !searchOpen && !condensed && hasPopover,
    interactive: true,
    onOpen: () => rowPopover?.activate(tileId),
    onClose: () => rowPopover?.leaveTile(tileId),
  });
  const active = intent.open && (!rowPopover || rowPopover.activeId === tileId);
  const rowActiveId = rowPopover?.activeId;
  const dismissIntent = intent.dismiss;
  useEffect(() => {
    if (rowActiveId != null && rowActiveId !== tileId) dismissIntent();
  }, [rowActiveId, tileId, dismissIntent]);
  useEffect(() => {
    if (searchOpen || condensed) {
      setHovered(false);
    }
  }, [searchOpen, condensed]);

  const animated = useCountUp(
    typeof rawValue === "number" ? rawValue : 0,
    animationKey,
    { index: tileIndex, ...(countFromZero ? { from: 0 } : {}), disabled: condensed },
  );
  const display = typeof rawValue === "number" ? formatter(animated) : value;
  const showBreakdown = !searchOpen
    && active
    && Array.isArray(breakdown)
    && breakdown.length > 0;
  const showTooltip = active
    && explanation != null
    && (typeof explanation !== "string" || explanation.length > 0)
    && !showBreakdown;

  useLayoutEffect(() => {
    if (!collection || condensed || (!showTooltip && !showBreakdown)) {
      setPortalPosition(null);
      return undefined;
    }
    const place = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const requested = showTooltip ? Number.parseFloat(tooltipStyle?.width) : rect.width;
      const width = Math.min(Number.isFinite(requested) ? requested : rect.width, window.innerWidth - 24);
      setPortalPosition({
        position: "fixed", top: rect.bottom + 6,
        left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
        width, right: "auto",
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [collection, condensed, showTooltip, showBreakdown, tooltipStyle]);

  useLayoutEffect(() => {
    if (!showTooltip || tooltipPlacement !== "viewport-clamped") {
      setTooltipAnchorLeft(null);
      return undefined;
    }

    const updatePosition = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const requestedWidth = Number.parseFloat(tooltipStyle?.width);
      const panelWidth = Math.min(
        Number.isFinite(requestedWidth) ? requestedWidth : rect.width,
        Math.max(0, viewportWidth - 24),
      );
      const spaceRight = viewportWidth - rect.right;
      const spaceLeft = rect.left;
      const alignLeft = spaceRight < panelWidth && spaceLeft > spaceRight;
      const preferredLeft = alignLeft ? rect.right - panelWidth : rect.left;
      const viewportLeft = Math.max(12, Math.min(preferredLeft, viewportWidth - panelWidth - 12));
      const anchorLeft = viewportLeft - rect.left;
      setTooltipAnchorLeft((current) => current === anchorLeft ? current : anchorLeft);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [showTooltip, tooltipPlacement, tooltipStyle]);

  // KPI-SCOPE-INDICATOR (G-22): border and shadow change when scope=responsive
  // and isFiltered — soft blue ring replaces the neutral card shadow.
  const borderStyle = (scope === "responsive" && isFiltered)
    ? `1px solid ${T.blueSoftBorder}`
    : "1px solid var(--v2-line)";
  const shadowStyle = (scope === "responsive" && isFiltered)
    ? (hovered ? "var(--v2-shadow-drop), 0 3px 12px rgba(47,91,214,.12)" : "0 3px 12px rgba(47,91,214,.12)")
    : (hovered ? "var(--v2-shadow-drop)" : "var(--v2-shadow-card)");

  if (condensed) return <CondensedKpiMetric label={label} value={typeof rawValue === "number" ? formatter(rawValue) : value} secondary={secondary}
    detail={descriptor ?? sub ?? (scope === "portfolio" ? "portfolio" : undefined)}
    tooltip={explanation} breakdown={breakdown} onActivate={onActivate} activationLabel={activationLabel}
    color={color} scope={scope} isFiltered={isFiltered} testId={testId} />;

  // Collection rows scroll horizontally at narrow widths. Keep the same
  // full-tile popovers, but lift them out of that overflow clipping boundary.
  const panel = (node) => collection ? createPortal(node, document.body) : node;
  return (
    <div
      ref={wrapperRef}
      {...intent.triggerProps}
      style={{ position: "relative", zIndex: active ? LAYERS.kpiPopover : undefined, height: height != null ? height : "100%", minWidth: 0 }}
      onMouseEnter={() => {
        intent.triggerProps.onMouseEnter();
        if (!searchOpen) {
          setHovered(true);
        }
      }}
      onMouseLeave={(event) => {
        setHovered(false);
        intent.triggerProps.onMouseLeave(event);
      }}
    >
      <div
        data-testid={testId}
        tabIndex={onActivate || explanation ? 0 : undefined}
        role={onActivate ? "button" : explanation ? "group" : undefined}
        aria-label={activationLabel ?? (explanation ? label : undefined)}
        aria-describedby={showTooltip ? panelId : undefined}
        onClick={onActivate}
        onKeyDown={onActivate ? (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onActivate();
          }
        } : undefined}
        style={{
          background: "var(--v2-card)",
          border: borderStyle,
          borderRadius: 14,
          boxShadow: shadowStyle,
          padding: "8px 14px 14px",
          boxSizing: "border-box",
          transform: hovered ? "translateY(-2px)" : "none",
          transition: "box-shadow .18s var(--v2-ease), transform .18s var(--v2-ease), border-color .18s",
          cursor: onActivate ? "pointer" : hasPopover ? "help" : "default",
          height: height != null ? height : "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          overflow: "visible",
        }}
      >
        {/* Zone A — label: single non-wrapping line; explained tiles have no
            native title competing with the whole-tile tooltip. */}
        <div
          title={explanation ? undefined : label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            minWidth: 0,
            fontSize: T.fsXxs5,
            color: "var(--v2-mid)",
            fontWeight: T.fwBold,
            textTransform: "uppercase",
            letterSpacing: ".03em",
            lineHeight: 1.45,
            whiteSpace: "nowrap",
            ...labelStyle,
          }}
        >
          <span title={explanation ? undefined : label} style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{label}</span>
        </div>
        {/* Zone B — number: constant gap above so baseline is always the same distance from tile top.
            whiteSpace:nowrap keeps the value on one line; no overflow clipping so numbers are
            always fully visible (tile wraps before crushing the number). */}
        <div style={{ marginTop: 2, fontSize: T.fsXl, fontWeight: T.fwBold, color: color || "var(--v2-dark)", lineHeight: 1, whiteSpace: "nowrap", display: "flex", alignItems: "baseline", gap: 6, ...valueStyle }}>
          {display}
          {secondary ? (
            <span
              data-testid={testId ? `${testId}-secondary` : "kpi-tile-secondary"}
              style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: "var(--v2-mid)", lineHeight: 1.2 }}
            >
              {secondary}
            </span>
          ) : null}
        </div>
        {/* Zone C — descriptor and scope cue share the existing sub-text row.
            The descriptor can shrink and truncate; the cue never shrinks. */}
        {scope === "responsive" && descriptor ? (
          <div
            style={{
              marginTop: "auto",
              paddingTop: 2,
              fontSize: T.fsXs,
              display: "flex",
              alignItems: "center",
              gap: 5,
              minWidth: 0,
              color: "var(--v2-light)",
            }}
          >
            <div
              title={explanation ? undefined : descriptor}
              style={{
                minWidth: 0,
                flex: "1 1 auto",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {descriptor}
            </div>
            {isFiltered ? (
              <span
                data-testid={testId ? `${testId}-scope-cue` : "kpi-tile-scope-cue"}
                title={explanation ? undefined : "Reflects the current view"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  flexShrink: 0,
                  padding: "1px 5px",
                  borderRadius: 5,
                  background: T.blueBg,
                  color: T.blue,
                  border: `1px solid ${T.blueSoftBorder}`,
                  fontWeight: T.fwBold,
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                }}
              >
                <FunnelIcon filtered />
                Filtered
              </span>
            ) : (
              <span
                data-testid={testId ? `${testId}-scope-cue` : "kpi-tile-scope-cue"}
                title={explanation ? undefined : "Reflects the current view"}
                style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}
              >
                <FunnelIcon />
              </span>
            )}
          </div>
        ) : scope === "portfolio" && descriptor ? (
          <div
            title={explanation ? undefined : descriptor}
            style={{
              marginTop: "auto",
              paddingTop: 2,
              fontSize: T.fsXs,
              color: "var(--v2-light)",
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {descriptor}
          </div>
        ) : scope === "responsive" ? (
          <div
            style={{
              marginTop: "auto",
              paddingTop: 2,
              fontSize: T.fsXs,
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: isFiltered ? "var(--v2-accent)" : "var(--v2-light)",
              fontWeight: isFiltered ? T.fwBold : undefined,
            }}
          >
            <FunnelIcon filtered={isFiltered} />
            {isFiltered ? "in view · filtered" : "in view"}
          </div>
        ) : scope === "portfolio" ? (
          <div
            style={{
              marginTop: "auto",
              paddingTop: 2,
              fontSize: T.fsXs,
              color: "var(--v2-light)",
            }}
          >
            portfolio
          </div>
        ) : sub ? (
          <div
            style={{
              marginTop: "auto",
              paddingTop: 2,
              fontSize: T.fsXs,
              color: "var(--v2-light)",
            }}
          >
            {sub}
          </div>
        ) : null}
      </div>

      {/* Tooltip panel — free-text prose explanation, floats below tile on hover */}
      {showTooltip && panel(
        <Tooltip
          {...intent.popupProps}
          id={panelId}
          data-testid={testId ? panelId : "kpi-tile-tooltip"}
          style={{
            top:       "calc(100% + 6px)",
            left:      0,
            width:     "100%",
            boxSizing: "border-box",
            whiteSpace: "normal",
            overflowWrap: "break-word",
            lineHeight: 1.5,
            padding:   "8px 12px",
            zIndex:    LAYERS.kpiPopover,
            pointerEvents: "auto",
            ...tooltipStyle,
            ...(tooltipPlacement === "viewport-clamped" && tooltipAnchorLeft != null
              ? { left: `${tooltipAnchorLeft}px` }
              : {}),
            ...(collection ? portalPosition : {}),
          }}
        >
          {explanation}
        </Tooltip>
      )}

      {/* Breakdown panel — floats below tile, does not affect tile or row height */}
      {showBreakdown && panel(
        <div
          {...intent.popupProps}
          data-testid={testId ? `${testId}-breakdown` : "kpi-tile-breakdown"}
          style={{
            position:     "absolute",
            top:          "calc(100% + 6px)",
            left:         0,
            right:        0,
            background:   "var(--v2-card)",
            border:       "1px solid var(--v2-line)",
            borderRadius: 10,
            boxShadow:    "var(--v2-shadow-drop)",
            padding:      "8px 12px",
            zIndex:       LAYERS.kpiPopover,
            display:      "flex",
            flexDirection:"column",
            gap:          4,
            ...(collection ? portalPosition : {}),
          }}
        >
          {breakdown.map((row) => (
            <div
              key={row.label}
              style={{
                display:        "flex",
                justifyContent: "space-between",
                alignItems:     "baseline",
                gap:            8,
              }}
            >
              <span style={{ fontSize: T.fsXs, color: "var(--v2-mid)", whiteSpace: "nowrap" }}>
                {row.label}
              </span>
              <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: "var(--v2-dark)", whiteSpace: "nowrap" }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
